import { Installments } from 'components/Cart/OrderSummary/types';
import {
  IBundle,
  IIncludedProduct,
  InstallmentPlanPricing,
  IProduct,
  IProductPricing as DCSProductPricing,
  ISoftwarePlan,
} from 'services/dcs/bundle';
import { ICartPricing, OrderType, IProductPricing, IProductsWithSoftwarePlan } from './useDCSTypes';
import { BooleanFilter } from '../utils/TypeUtils';

const getTotalForInstallmentPlan = (
  installmentPlanCode: Installments,
  includedProducts: IIncludedProduct[],
) => {
  return includedProducts.reduce((sum, { product, quantity }) => {
    const pricing = product.pricing.find(p => p.installmentPlan.code === installmentPlanCode);
    if (!pricing) return;

    return sum + pricing.price.value * quantity;
  }, 0);
};

export const getInstallmentPlanMap = (productPricing: DCSProductPricing[]) => {
  return productPricing.reduce((map, pricing) => {
    if (pricing.installmentPlan && pricing.installmentPlan.code !== Installments.P) {
      map[pricing.installmentPlan.code] = pricing.price;
    }
    return map;
  }, {});
};

export const getIncludedProductIds = (cartPricing: ICartPricing) => {
  return cartPricing.includedProducts?.map(({ product }) => product.id) ?? [];
};

export const getRecommendedAddonIds = (cartPricing: ICartPricing) => {
  return cartPricing.recommendedAddons.map(({ product }) => product.id);
};

export const getProductPriceDetail = (product?: IProduct): IProductPricing | null => {
  if (!product) return null;

  const pricing = product.pricing.find(pricing => pricing.installmentPlan.code === Installments.P);

  if (!pricing) return null;

  const basePrice = pricing.price;
  const installmentPlans: InstallmentPlanPricing = getInstallmentPlanMap(product.pricing);

  const {
    id,
    name,
    msrp,
    isSelfServicePurchaseEnabled,
    isIncludedInMultiDevicePricing,
    fdmpId,
    supportedSoftwarePlans,
    accessories,
  } = product;

  return {
    id,
    name,
    basePrice,
    msrp,
    installmentPlans,
    isSelfServicePurchaseEnabled,
    isIncludedInMultiDevicePricing,
    fdmpId,
    supportedSoftwarePlans,
    accessories,
  } as IProductPricing;
};

export const getBundlePriceDetail = (bundle?: IBundle): ICartPricing | null => {
  if (!bundle) return null;

  const pricing = bundle.productPricing.find(pricing => pricing.installmentPlan.code === Installments.P);

  if (!pricing) return null;

  const basePrice = pricing.price;
  const baseMonthlyPrice = bundle.softwarePlanPricing.baseMonthlyPrice;
  const monthlyAdditionalPrice = bundle.softwarePlanPricing.monthlyPrice;
  const additionalDevicePrice = bundle.softwarePlanPricing.additionalDevicePrice;
  const installmentPlans: InstallmentPlanPricing = getInstallmentPlanMap(bundle.productPricing);

  const { keyedInRate, inPersonRate } = bundle.softwarePlanPricing.transactionRates;
  const { id, name, msrp, includedProducts, recommendedAddons, softwarePlan, isSelfServicePurchaseEnabled } =
    bundle;

  return {
    id,
    name,
    basePrice,
    baseMonthlyPrice,
    monthlyAdditionalPrice,
    msrp,
    keyedInRate,
    inPersonRate,
    installmentPlans,
    softwarePlan,
    includedProducts,
    recommendedAddons,
    isSelfServicePurchaseEnabled,
    additionalDevicePrice,
    orderType: OrderType.BUNDLE,
  } as ICartPricing;
};

export function mapDCSProductsToPricings(products: IProduct[]): IProductPricing[] {
  return products.map(getProductPriceDetail).filter(BooleanFilter);
}

export function mapDCSBundlesToPricings(bundles: IBundle[]): ICartPricing[] {
  return bundles.map(getBundlePriceDetail).filter(BooleanFilter);
}

/**
Maps products with software plans to cart pricing for BYO.
@param {IProductsWithSoftwarePlan} productsWithSoftwarePlan - The products with software plan to be mapped.
@param {ISoftwarePlan[]} dcsSoftwarePlans - The available software plans.
@param {IProduct[]} dcsProducts - The available products.
@returns {ICartPricing} - The cart pricing object.
@throws {Error} - If the software plan does not contain any of the given products or if the root product has no pricing information.
*/
export const mapProductsWithSoftwarePlanToCartPricing = (
  productsWithSoftwarePlan: IProductsWithSoftwarePlan,
  dcsSoftwarePlans: ISoftwarePlan[],
  dcsProducts: IProduct[],
): ICartPricing | undefined => {
  const dcsSoftwarePlan = dcsSoftwarePlans.find(sp => sp.id === productsWithSoftwarePlan.planId);
  if (!dcsSoftwarePlan) return;

  const includedProducts = productsWithSoftwarePlan.products.reduce(
    (acc: IIncludedProduct[], product: { id: string; quantity: number }) => {
      const includedProduct = dcsProducts.find(dp => dp.id === product.id);

      if (includedProduct) {
        acc.push({ product: includedProduct, quantity: product.quantity });
      }

      return acc;
    },
    [],
  );

  if (includedProducts.length === 0) {
    const errorMessage = `The software plan '${productsWithSoftwarePlan.planId}' does not contain any of the given products.`;
    throw new Error(errorMessage);
  }

  const rootProduct = includedProducts[0].product;
  const pricing = rootProduct.pricing.find(p =>
    [Installments.P, Installments.SL36, Installments.SL48].includes(p.installmentPlan.code),
  );

  if (!pricing) {
    const errorMessage = `The product '${rootProduct.id}' does not have any pricing information.`;
    throw new Error(errorMessage);
  }

  const filteredProductsWithoutInstallmentPlans = includedProducts.filter(p => {
    const filteredProductsInstallmentPlans = p.product.pricing.map(
      productPricing => productPricing.installmentPlan.code,
    );

    return (
      filteredProductsInstallmentPlans.includes(Installments.SL36) ||
      filteredProductsInstallmentPlans.includes(Installments.SL48)
    );
  });

  const installmentPlansPricing =
    filteredProductsWithoutInstallmentPlans.length > 0
      ? filteredProductsWithoutInstallmentPlans[0].product.pricing
          .map(p => {
            const totalPrice = getTotalForInstallmentPlan(
              p.installmentPlan.code,
              filteredProductsWithoutInstallmentPlans,
            );

            if (!totalPrice) return;

            return {
              installmentPlan: p.installmentPlan,
              price: {
                value: totalPrice,
                currencyCode: p.price.currencyCode,
              },
              priceOverrides: p.priceOverrides,
            };
          })
          .filter(BooleanFilter)
      : [];

  const installmentPlans: InstallmentPlanPricing = getInstallmentPlanMap(installmentPlansPricing);
  const additionalDevicePrice = dcsSoftwarePlan.pricing?.additionalDevicePrice;

  return {
    softwarePlan: dcsSoftwarePlan,
    includedProducts,
    recommendedAddons: [],
    id: 'BUILD_YOUR_OWN',
    name: 'Your Clover system',
    msrp: rootProduct.msrp,
    basePrice: pricing.price,
    installmentPlans,
    additionalDevicePrice,
    isSelfServicePurchaseEnabled: rootProduct.isSelfServicePurchaseEnabled,
    baseMonthlyPrice: dcsSoftwarePlan.pricing?.baseMonthlyPrice,
    monthlyAdditionalPrice: dcsSoftwarePlan.pricing?.monthlyPrice,
    keyedInRate: dcsSoftwarePlan.pricing?.transactionRates.keyedInRate,
    inPersonRate: dcsSoftwarePlan.pricing?.transactionRates.inPersonRate,
    orderType: OrderType.BYO,
  } as ICartPricing;
};
